/**
 * GridOverlay — Squarespace Fluid Engine-style grid renderer.
 *
 * Iter 164: tightened the grid to 48-col × 20-px (was 24-col × 60-px) so
 * users can position elements with near-pixel precision while still
 * benefiting from snap alignment.
 *
 * Iter 145 (Session 1 of Path A): visual grid that appears ONLY when a
 * section is being edited (hovered, selected, OR an element inside it is
 * being dragged).
 *
 * Rendering strategy:
 *   • One absolutely-positioned <div> per section, behind the content
 *     (z-index 1, content stays at z 10+).
 *   • Background image is a CSS linear-gradient repeating 48 times —
 *     drawn with subtle gray-on-transparent so the live preview isn't
 *     polluted. The grid auto-adapts to any section width.
 *   • Visibility is controlled by `data-wb-grid-active="1"` on the host
 *     section. The hook `useGridOverlay` toggles that attribute on
 *     hover/selection/drag.
 *
 * Responsive:
 *   • Desktop (≥1024px) → 48 columns × 20px rows
 *   • Tablet  (≥640px)  → 24 columns
 *   • Mobile  (<640px)  → 12 columns (auto-stacked feel)
 *
 * Pure presentational. No state. No event handlers. Doesn't intercept
 * pointer events — `pointer-events: none`.
 */
import React from 'react';

const STYLE_ID = 'wb-grid-overlay-style';
const GRID_COLOR = 'rgba(139, 92, 246, 0.10)';      // soft purple
const GRID_COLOR_STRONG = 'rgba(139, 92, 246, 0.22)'; // active drag state

export function ensureGridOverlayStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    /* Grid is drawn inside this absolutely-positioned helper div.
       The host section owns position:relative already (FullBlock).
       Iter 198e — layered 24-col (heavier) + 48-col (faint) so the
       Squarespace-style 24-column framework is visually anchored. */
    .wb-grid-overlay {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 1;
      opacity: 0;
      transition: opacity 200ms ease;
      background-image:
        repeating-linear-gradient(to right,
          rgba(139, 92, 246, 0.22) 0,
          rgba(139, 92, 246, 0.22) 1px,
          transparent 1px,
          transparent calc(100% / 24)),
        repeating-linear-gradient(to right,
          rgba(139, 92, 246, 0.08) 0,
          rgba(139, 92, 246, 0.08) 1px,
          transparent 1px,
          transparent calc(100% / 48));
    }
    [data-wb-grid-active="1"] .wb-grid-overlay { opacity: 1; }
    [data-wb-grid-dragging="1"] .wb-grid-overlay {
      opacity: 1;
      background-image:
        repeating-linear-gradient(to right,
          rgba(139, 92, 246, 0.42) 0,
          rgba(139, 92, 246, 0.42) 1px,
          transparent 1px,
          transparent calc(100% / 24)),
        repeating-linear-gradient(to right,
          ${GRID_COLOR_STRONG} 0,
          ${GRID_COLOR_STRONG} 1px,
          transparent 1px,
          transparent calc(100% / 48)),
        repeating-linear-gradient(to bottom,
          ${GRID_COLOR_STRONG} 0,
          ${GRID_COLOR_STRONG} 1px,
          transparent 1px,
          transparent 20px);
    }
    @media (max-width: 1024px) {
      .wb-grid-overlay { background-size: 100% 100% !important; }
      [data-wb-grid-active="1"] .wb-grid-overlay {
        background-image:
          repeating-linear-gradient(to right,
            ${GRID_COLOR} 0,
            ${GRID_COLOR} 1px,
            transparent 1px,
            transparent calc(100% / 24));
      }
    }
    @media (max-width: 640px) {
      [data-wb-grid-active="1"] .wb-grid-overlay {
        background-image:
          repeating-linear-gradient(to right,
            ${GRID_COLOR} 0,
            ${GRID_COLOR} 1px,
            transparent 1px,
            transparent calc(100% / 12));
      }
    }

    /* Section-resize handle pinned to bottom-center of each section. */
    .wb-section-resize-handle {
      position: absolute;
      left: 50%;
      bottom: -8px;
      transform: translateX(-50%);
      width: 64px;
      height: 16px;
      background: #0E0E23;
      color: #fff;
      border-radius: 999px;
      display: none;
      align-items: center;
      justify-content: center;
      cursor: ns-resize;
      font-size: 10px;
      letter-spacing: 1px;
      font-weight: 700;
      text-transform: uppercase;
      box-shadow: 0 6px 18px rgba(14,14,35,0.40);
      z-index: 40;
      user-select: none;
      gap: 4px;
    }
    [data-wb-grid-active="1"] .wb-section-resize-handle { display: flex; }
    .wb-section-resize-handle:hover { background: #8B5CF6; }

    /* Snap-guide lines (rendered globally on document.body during drag).
       Iter 165 — cyan + thicker glow, Figma-style.
       Iter 198e — brighter, thicker, plus a badge pill anchored to each. */
    .wb-snap-guide-v {
      position: fixed;
      top: 0;
      bottom: 0;
      width: 1px;
      background: #22D3EE;
      box-shadow: 0 0 12px #22D3EE, 0 0 3px #22D3EE;
      pointer-events: none;
      z-index: 9999;
    }
    .wb-snap-guide-h {
      position: fixed;
      left: 0;
      right: 0;
      height: 1px;
      background: #22D3EE;
      box-shadow: 0 0 12px #22D3EE, 0 0 3px #22D3EE;
      pointer-events: none;
      z-index: 9999;
    }
    .wb-snap-badge {
      position: fixed;
      z-index: 10000;
      padding: 3px 8px;
      border-radius: 999px;
      background: rgba(20, 16, 25, 0.95);
      color: #22D3EE;
      border: 1px solid rgba(34, 211, 238, 0.4);
      font: 700 9px/1 system-ui, sans-serif;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      pointer-events: none;
      box-shadow: 0 4px 12px rgba(0,0,0,0.4), 0 0 8px rgba(34,211,238,0.3);
    }

    /* ─── Iter 157 — InlineText hover/focus model + toolbar-priority CSS ───
       Hover-on-text shows a grab cursor + a subtle outline (NOT box-shadow
       ring) so it never shifts layout. Once focused (real click → edit
       intent), cursor switches to text. Body[data-wb-toolbar-active] is
       set by DashboardContent when a higher-priority toolbar (text/image/
       button) is showing — section-card + section-hover-bar fade out so
       only ONE toolbar context is active at a time. */
    /* ─── Iter 189i — Hover-only text highlight (user request: highlight
       must appear ONLY on hover, disappear on click).
       - On hover:  1px violet outline (never shifts layout, no flicker
                    because we do NOT use box-shadow/background).
       - On focus:  outline collapses to transparent so the "clicked =
                    editing" state has NO chrome around the caret.
       - Focus wins over hover — using :hover:not(:focus) prevents the
         hover outline from reappearing while the caret is still active. */
    .wb-inline-text { cursor: grab; outline: 1px solid transparent; outline-offset: 2px; }
    .wb-inline-text:hover:not(:focus) { outline-color: rgba(139,92,246,0.55); }
    .wb-inline-text:focus { cursor: text; outline-color: transparent; }
    /* Iter 190e — Placeholder rendering for empty InlineText elements.
       Without this, an empty <p> collapses to height 0 → not visible,
       not grabbable, and useFluidTextDrag cannot register a mousedown
       on it. Rendering the placeholder text via ::before gives the
       element intrinsic dimensions AND signals "click to type". */
    .wb-inline-text[data-placeholder]:empty:not(:focus)::before {
      content: attr(data-placeholder);
      color: #aaa;
      font-style: italic;
      pointer-events: none;
    }
    body[data-wb-toolbar-active="text"] [data-testid^="section-control-card-"],
    body[data-wb-toolbar-active="text"] [data-testid^="section-controls-"],
    body[data-wb-toolbar-active="image"] [data-testid^="section-control-card-"],
    body[data-wb-toolbar-active="image"] [data-testid^="section-controls-"] {
      opacity: 0 !important;
      pointer-events: none !important;
    }

    /* Iter 178l: eliminate hover-flicker on section chrome.
       Previously the Add Block pill + slim section-controls used
       opacity-0 group-hover/section:opacity-100 transition-all which
       (a) ran a 150ms fade-in animation that visibly strobed when any
       upstream React state churn fired during the transition window, and
       (b) kept the elements at opacity 0 but still in the layout, where
       they could receive mouse events at the wrong moment.

       New model: chips are visibility:hidden + pointer-events:none by
       default. On :hover of the OWNING SECTION they flip to visible +
       auto. No CSS transition, no animation churn. The block.id stays in
       the DOM so layout is stable, but the chip can NEVER intercept
       events while hidden. */
    /* Iter 178o — Hover-flicker triage. After multiple iterations chasing
       this bug in different overlays (section chrome, image-upload-zone,
       pointer-events traps), we have a stationary-mouse render-loop variant
       that's bypassing the visibility-based show/hide. Pragmatic decision:
       MAKE THE WB-HOVER-CHIP CLASS ALWAYS-VISIBLE so editors can actually
       edit. The visual distraction of a permanent chip is far less harmful
       than 5-10 Hz strobing that prevents text selection. */
    .wb-hover-chip {
      visibility: visible;
      opacity: 1;
      pointer-events: none;
    }
    .wb-hover-chip-clickable {
      pointer-events: auto;
    }
    /* Iter 178o — Image-overlay: hover-driven, no transition (so no
       animation-restart strobing). Container always stays
       pointer-events:none so it never intercepts the mouse and never
       creates the pointer-events-auto loop. Inner buttons opt in via
       inline pointer-events-auto. */
    .wb-img-overlay {
      visibility: hidden;
      opacity: 0;
    }
    .group:hover > .wb-img-overlay {
      visibility: visible;
      opacity: 1;
    }

    /* ─── Iter 159 — Disable hover transforms inside the builder canvas.
       Pricing cards and feature cards use Tailwind classes like
       hover:-translate-y-1 and hover:scale-110 which look great on the
       live site but cause editor flicker: card translates under the
       mouse, mouse appears below the card edge, hover ends, card snaps
       back, mouse over card again — 60Hz loop. Inside the builder we
       neutralise transform on hover so vendors can edit cards stably.
       The live published site is unaffected. */
    [data-testid="content-editor"] *:hover {
      transform: none !important;
    }

    /* ─── Iter 156 — Portal-rendered grid overlay + resize handle ───
       Lives at document.body via React.createPortal. Visibility is
       keyed off body[data-wb-grid-dragging] since the portal isn't a
       child of any section.
       Iter 198e — Layered grid: FAINT 48-col + STRONGER 24-col every
       other line, so vendors see the Squarespace-style 24-column
       framework while retaining the 48-col fine-grain snap. */
    .wb-grid-overlay-portal {
      opacity: 0;
      transition: opacity 200ms ease;
      pointer-events: none;
      background-image:
        /* 24-col heavier lines */
        repeating-linear-gradient(to right,
          rgba(139, 92, 246, 0.35) 0,
          rgba(139, 92, 246, 0.35) 1px,
          transparent 1px,
          transparent calc(100% / 24)),
        /* 48-col faint lines */
        repeating-linear-gradient(to right,
          rgba(139, 92, 246, 0.14) 0,
          rgba(139, 92, 246, 0.14) 1px,
          transparent 1px,
          transparent calc(100% / 48)),
        /* Row bands */
        repeating-linear-gradient(to bottom,
          ${GRID_COLOR_STRONG} 0,
          ${GRID_COLOR_STRONG} 1px,
          transparent 1px,
          transparent 20px);
    }
    body[data-wb-grid-dragging="1"] .wb-grid-overlay-portal { opacity: 1; }

    .wb-section-resize-handle-portal {
      background: #0E0E23;
      color: #fff;
      border-radius: 999px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: ns-resize;
      font-size: 10px;
      letter-spacing: 1px;
      font-weight: 700;
      text-transform: uppercase;
      box-shadow: 0 6px 18px rgba(14,14,35,0.40);
      user-select: none;
      gap: 4px;
      opacity: 0;
      transition: opacity 200ms ease, background 150ms ease;
      border: none;
      gap: 4px;
    }
    .wb-section-resize-handle-portal:hover,
    body[data-wb-grid-dragging="1"] .wb-section-resize-handle-portal {
      opacity: 1;
      background: #8B5CF6;
    }
    /* Faint hint on canvas hover so the user can find the handle. */
    [data-testid="content-editor"]:hover ~ * .wb-section-resize-handle-portal,
    [data-wb-fluid-overlay-layer="1"]:hover .wb-section-resize-handle-portal {
      opacity: 0.35;
    }
  `;
  document.head.appendChild(el);
}

export default function GridOverlay() {
  return <div className="wb-grid-overlay" aria-hidden="true" />;
}
